import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Play, Pause, Music } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SpotifyPlayer({ onConnectionChange }) {
  const [player, setPlayer] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(true);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const playerCheckInterval = useRef(null);
  const lastPlaybackState = useRef(null); // Store last playback state for recovery
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  useEffect(() => {
    // Delay Spotify initialization to prevent blocking page load
    const initTimeout = setTimeout(() => {
      checkSpotifyConnection();
    }, 2000);
    
    // Check connection every 30 seconds to ensure it stays alive and auto-reconnect
    const interval = setInterval(() => {
      checkSpotifyConnection();
      console.log('Spotify connection check...');
    }, 30 * 1000);
    
    // Refresh token every 50 minutes (tokens expire after 1 hour)
    const tokenRefreshInterval = setInterval(() => {
      refreshSpotifyToken();
    }, 50 * 60 * 1000);
    
    // Listen for page visibility changes (e.g., coming back from screensaver)
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        console.log('Page visible again, checking Spotify connection...');
        
        // First refresh the token - it may have expired during screensaver
        await refreshSpotifyToken();
        
        // Then check connection status
        await checkSpotifyConnection();
        
        // Try to reconnect player if it exists
        if (player) {
          reconnectAttempts.current = 0;
          attemptReconnect();
        }
      } else {
        // Page is hidden - save current playback state
        if (player) {
          try {
            const state = await player.getCurrentState();
            if (state && state.track_window?.current_track) {
              lastPlaybackState.current = {
                track_uri: state.track_window.current_track.uri,
                context_uri: state.context?.uri,
                position_ms: state.position,
                paused: state.paused
              };
              console.log('Saved playback state before screensaver:', lastPlaybackState.current);
            }
          } catch (err) {
            console.log('Could not save playback state:', err);
          }
        }
      }
    };
    
    // Helper function to attempt reconnect with retry logic
    const attemptReconnect = async () => {
      if (reconnectAttempts.current >= maxReconnectAttempts) {
        console.log('Max reconnect attempts reached');
        return;
      }
      
      try {
        const success = await player?.connect();
        if (success) {
          console.log('Player reconnected on visibility change');
          reconnectAttempts.current = 0;
          
          // Try to restore playback state after a short delay
          if (lastPlaybackState.current && !lastPlaybackState.current.paused) {
            setTimeout(async () => {
              try {
                await restorePlaybackState();
              } catch (err) {
                console.log('Could not restore playback state:', err);
              }
            }, 1000);
          }
        } else {
          reconnectAttempts.current++;
          console.log(`Reconnect attempt ${reconnectAttempts.current} failed, retrying...`);
          setTimeout(attemptReconnect, 2000);
        }
      } catch (err) {
        console.log('Reconnect error:', err);
        reconnectAttempts.current++;
        setTimeout(attemptReconnect, 2000);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearTimeout(initTimeout);
      clearInterval(interval);
      clearInterval(tokenRefreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (accessToken) {
      try {
        initializePlayer();
      } catch (error) {
        console.error('Failed to initialize Spotify player:', error);
        // Silently fail - app will work without Spotify
      }
    }
    
    return () => {
      if (player) {
        try {
          player.disconnect();
        } catch (err) {
          console.log('Error disconnecting player:', err);
        }
      }
    };
  }, [accessToken]);

  const refreshSpotifyToken = async () => {
    try {
      console.log('Refreshing Spotify token...');
      const response = await axios.post(`${API}/spotify/auth/refresh`);
      if (response.data.access_token) {
        setAccessToken(response.data.access_token);
        console.log('Spotify token refreshed successfully');
        return response.data.access_token;
      }
    } catch (error) {
      console.error('Error refreshing Spotify token:', error);
      // If refresh fails, try to check connection status
      checkSpotifyConnection();
    }
    return null;
  };
  
  // Restore playback state after reconnection
  const restorePlaybackState = async () => {
    const savedState = lastPlaybackState.current;
    if (!savedState || !deviceId || !accessToken) {
      console.log('Cannot restore playback - missing state or credentials');
      return;
    }
    
    console.log('Attempting to restore playback state:', savedState);
    
    try {
      const playBody = savedState.context_uri 
        ? { context_uri: savedState.context_uri, position_ms: savedState.position_ms }
        : { uris: [savedState.track_uri], position_ms: savedState.position_ms };
      
      const response = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
        method: 'PUT',
        body: JSON.stringify(playBody),
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
      });
      
      if (response.ok || response.status === 204) {
        console.log('Playback state restored successfully');
        toast.success('Music resumed');
        
        // Restore volume
        if (player) {
          await player.setVolume(0.20);
        }
        
        // Clear saved state
        lastPlaybackState.current = null;
      } else {
        console.log('Could not restore playback:', response.status);
      }
    } catch (error) {
      console.error('Error restoring playback:', error);
    }
  };
  
  // Expose restore function globally
  useEffect(() => {
    window.restoreSpotifyPlayback = restorePlaybackState;
    return () => {
      delete window.restoreSpotifyPlayback;
    };
  }, [deviceId, accessToken]);

  const checkSpotifyConnection = async () => {
    try {
      const response = await axios.get(`${API}/spotify/auth/status`);
      if (response.data.connected) {
        const newToken = response.data.access_token;
        // Only update if token changed to avoid unnecessary re-renders
        if (newToken !== accessToken) {
          setAccessToken(newToken);
        }
        if (onConnectionChange) onConnectionChange(true);
        if (window.setSpotifyConnected) window.setSpotifyConnected(true);
      } else {
        // Try to reconnect automatically
        console.log('Spotify disconnected, attempting auto-reconnect...');
        await refreshSpotifyToken();
      }
    } catch (error) {
      console.error('Error checking Spotify connection:', error);
      // Don't immediately disconnect on error - might be temporary network issue
      // Just log and wait for next check
    }
  };

  const initializePlayer = () => {
    try {
      if (!window.Spotify) {
        // Load Spotify SDK
        const script = document.createElement('script');
        script.src = 'https://sdk.scdn.co/spotify-player.js';
        script.async = true;
        script.onerror = () => {
          console.error('Failed to load Spotify SDK');
          toast.error('Spotify SDK failed to load');
        };
        document.body.appendChild(script);

        window.onSpotifyWebPlaybackSDKReady = () => {
          try {
            createPlayer();
          } catch (err) {
            console.error('Error in onSpotifyWebPlaybackSDKReady:', err);
          }
        };
      } else {
        createPlayer();
      }
    } catch (error) {
      console.error('Error initializing Spotify player:', error);
    }
  };

  const createPlayer = () => {
    try {
      const spotifyPlayer = new window.Spotify.Player({
      name: 'Dynamo Gymnastics Music Player',
      getOAuthToken: cb => {
        cb(accessToken);
      },
      volume: 0.20  // 25% default volume for background music
    });

    // Error handling
    spotifyPlayer.addListener('initialization_error', ({ message }) => {
      console.error('Initialization Error:', message);
      toast.error('Spotify player initialization failed');
    });

    spotifyPlayer.addListener('authentication_error', ({ message }) => {
      console.error('Authentication Error:', message);
      console.log('Attempting to refresh token automatically...');
      // Try to refresh token automatically instead of showing error immediately
      refreshSpotifyToken();
    });

    spotifyPlayer.addListener('account_error', ({ message }) => {
      console.error('Account Error:', message);
      toast.error('Spotify Premium required');
    });

    spotifyPlayer.addListener('playback_error', ({ message }) => {
      console.error('Playback Error:', message);
      // Only show error for actual failures, not transient issues
      // Common transient errors: context unavailable, device temporarily offline
      if (message && !message.includes('context') && !message.includes('temporarily')) {
        // Don't spam user with errors - log only
        console.log('Spotify playback error (likely transient)');
      }
    });

    // Ready
    spotifyPlayer.addListener('ready', ({ device_id }) => {
      console.log('Ready with Device ID', device_id);
      setDeviceId(device_id);
      // Set initial volume to 25% for background music
      spotifyPlayer.setVolume(0.20).then(() => {
        console.log('Initial volume set to 20%');
      });
      toast.success('Spotify connected!');
    });

    // Not Ready
    spotifyPlayer.addListener('not_ready', ({ device_id }) => {
      console.log('Device ID has gone offline', device_id);
      setDeviceId(null);
      
      // Try to reconnect immediately
      console.log('Attempting to reconnect Spotify player...');
      spotifyPlayer.connect().then(success => {
        if (success) {
          console.log('Spotify player reconnected successfully');
        } else {
          // If immediate reconnect fails, try again after 2 seconds
          setTimeout(() => {
            console.log('Retry reconnect...');
            spotifyPlayer.connect();
          }, 2000);
        }
      });
    });

    // Player state changed
    spotifyPlayer.addListener('player_state_changed', (state) => {
      try {
        if (!state || !state.track_window || !state.track_window.current_track) {
          setCurrentTrack(null);
          setIsActive(false);
          if (window.clearPlayerTrack) window.clearPlayerTrack();
          return;
        }

        const track = state.track_window.current_track;
        setCurrentTrack(track);
        setIsPaused(state.paused);
        setIsActive(true);

        // Update unified player - but ONLY if MP3 is not currently playing
        // Check if MP3 is active - if so, don't overwrite with Spotify info
        const mp3IsPlaying = window.currentMP3Audio && !window.currentMP3Audio.paused;
        const mp3Active = window.mp3PlaybackActive === true;
        
        if (window.updatePlayerTrack && track && track.name && !mp3IsPlaying && !mp3Active) {
          try {
            const artistsArray = Array.isArray(track.artists) ? track.artists : [];
            const artistNames = artistsArray.length > 0 ? artistsArray.map(a => a?.name || '').filter(Boolean).join(', ') : 'Unknown Artist';
            const albumImages = Array.isArray(track.album?.images) ? track.album.images : [];
            const imageUrl = albumImages.length > 0 && albumImages[0]?.url ? albumImages[0].url : null;
            
            console.log('SpotifyPlayer: Updating unified player with Spotify track');
            window.updatePlayerTrack({
              name: track.name,
              artist: artistNames,
              image: imageUrl
            }, 'spotify');
          } catch (err) {
            console.log('Error updating unified player with track info:', err);
          }
        } else if (mp3IsPlaying || mp3Active) {
          console.log('SpotifyPlayer: Skipping update - MP3 is currently playing or active');
        }
        if (window.updatePlayerPaused) {
          window.updatePlayerPaused(state.paused);
        }
      } catch (error) {
        console.error('Error in player_state_changed handler:', error);
      }

      // Auto-play if loaded but paused (fixes autoplay issue)
      try {
        if (state && state.paused && state.position === 0 && state.track_window && state.track_window.current_track) {
          console.log('Track loaded but paused, auto-resuming...');
          setTimeout(() => {
            spotifyPlayer.resume().then(() => {
              // Volume is controlled by user via UnifiedPlayer slider
            }).catch(err => console.log('Auto-resume error:', err));
          }, 100);
        }
      } catch (autoPlayError) {
        console.log('Auto-play check error:', autoPlayError);
      }
    });

    // Connect to the player
    spotifyPlayer.connect().then(success => {
      if (success) {
        console.log('The Web Playback SDK successfully connected to Spotify!');
        // Activate the player to enable autoplay
        spotifyPlayer.activateElement().then(() => {
          console.log('Player activated for autoplay');
        }).catch(err => {
          console.log('Player activation will happen on first user interaction');
        });
      }
    });

    setPlayer(spotifyPlayer);
    
    // Expose player globally for volume control
    window.spotifyPlayer = spotifyPlayer;
    } catch (error) {
      console.error('Error creating Spotify player:', error);
      toast.error('Failed to initialize Spotify player');
    }
  };

  const togglePlay = () => {
    if (player) {
      player.togglePlay();
    }
  };

  // Expose play method to parent
  useEffect(() => {
    window.playSpotifyPlaylist = async (playlistUri) => {
      if (!deviceId || !accessToken) {
        toast.error('Spotify not connected');
        return;
      }

      try {
        // Start playback with explicit play state
        const response = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
          method: 'PUT',
          body: JSON.stringify({ 
            context_uri: playlistUri,
            position_ms: 0
          }),
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
        });

        if (response.ok || response.status === 204) {
          // Enable shuffle for background playlists
          fetch(`https://api.spotify.com/v1/me/player/shuffle?state=true&device_id=${deviceId}`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${accessToken}` }
          }).catch(err => console.log('Shuffle enable error (non-critical):', err));

          // Wait a moment for Spotify to load the playlist
          setTimeout(async () => {
            // Ensure playback is actually playing
            if (player) {
              try {
                await player.resume();
                // Set initial volume to 25% for background music
                await player.setVolume(0.20);
                console.log('Playback resumed at 20% volume');
                
                // Also set via Web API as backup
                if (deviceId && accessToken) {
                  await fetch(`https://api.spotify.com/v1/me/player/volume?volume_percent=20&device_id=${deviceId}`, {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                  });
                }
                toast.success('Playing music!');
              } catch (err) {
                console.log('Resume error (might already be playing):', err);
                toast.success('Playing music!');
              }
            } else {
              toast.success('Playing music!');
            }
          }, 500);
        } else {
          const error = await response.json();
          console.error('Spotify API error:', error);
          // If player is not ready, try to activate it first
          if (error.error?.reason === 'NO_ACTIVE_DEVICE') {
            toast.info('Activating player...');
            // Give player a moment to activate
            setTimeout(() => window.playSpotifyPlaylist(playlistUri), 1500);
          } else {
            toast.error('Failed to play playlist');
          }
        }
      } catch (error) {
        console.error('Error playing playlist:', error);
        // Try to force play anyway
        if (player) {
          setTimeout(() => {
            player.resume().catch(() => {});
          }, 1000);
        }
        toast.error('Failed to play playlist');
      }
    };

    // Expose play track method for Dance Warmups
    window.playSpotifyTrack = async (trackUrl) => {
      if (!deviceId || !accessToken) {
        toast.error('Spotify not connected');
        return;
      }

      // Extract track URI from URL or use directly if it's already a URI
      let trackUri = trackUrl;
      if (trackUrl.includes('spotify.com')) {
        // Extract track ID from URL like https://open.spotify.com/track/{id}
        const match = trackUrl.match(/track\/([a-zA-Z0-9]+)/);
        if (match) {
          trackUri = `spotify:track:${match[1]}`;
        }
      }

      try {
        // Start playback with explicit play state
        const response = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
          method: 'PUT',
          body: JSON.stringify({ 
            uris: [trackUri],
            position_ms: 0
          }),
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
        });

        if (response.ok || response.status === 204) {
          // Wait a moment for Spotify to load the track
          setTimeout(async () => {
            // Ensure playback is actually playing
            if (player) {
              try {
                await player.resume();
                // Set volume to 35% for Dance Warmups (same as floor music)
                await player.setVolume(0.35);
                console.log('Playing track at 35% volume');
                
                // Also set via Web API as backup
                if (deviceId && accessToken) {
                  await fetch(`https://api.spotify.com/v1/me/player/volume?volume_percent=35&device_id=${deviceId}`, {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                  });
                }
                toast.success('Playing dance warmup!');
              } catch (err) {
                console.log('Resume error (might already be playing):', err);
                toast.success('Playing dance warmup!');
              }
            } else {
              toast.success('Playing dance warmup!');
            }
          }, 500);
        } else {
          const error = await response.json();
          console.error('Spotify API error:', error);
          // If player is not ready, try to activate it first
          if (error.error?.reason === 'NO_ACTIVE_DEVICE') {
            toast.info('Activating player...');
            // Give player a moment to activate
            setTimeout(() => window.playSpotifyTrack(trackUrl), 1500);
          } else {
            toast.error('Failed to play track');
          }
        }
      } catch (error) {
        console.error('Error playing track:', error);
        toast.error('Failed to play track');
      }
    };
  }, [deviceId, accessToken, player]);

  // Don't render this player bar anymore - UnifiedPlayer handles it
  return null;
}
